import React from 'react';
import { useParams } from 'react-router-dom';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import AddCommitmentsToEqualisation from './AddCommitmentsToEqualisation/index';
import { EqualisationSection } from 'further-types/lp/equalisation';
import EqualiseActions from './EqualiseActions';
import {
  EqualisationProvider,
  useEqualisationSection,
} from './EqualisationContext';
import EqualisationSummary from './EqualisationSummary';
import { CalendarDay } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualiseFees from './EqualiseFees';
import EqualiseActionLinkedCapitalActions from './EqualiseActionLinkedCapitalActions';
import EqualiseGeneralCapitalCalls from './EqualiseGeneralCapitalCalls';
import EqualisationInterest from './EqualisationInterest';
import EqualiseCapitalContributions from './EqualiseCapitalContributions';
import { SortOrder } from 'further-types/utils';

const EqualisationsEditorContent: React.FC<{
  equalisation?: {
    commitmentIds: Array<string>;
    name: string;
    closeDate: CalendarDay;
    baseCurrency: CurrencyCode;
    id: string;
    isDraft: boolean;
    selectedCommitments: Array<{
      commitmentId: string;
      investorName: string;
      isCarryPartnerCommitment: boolean;
      totalCommitmentLcy: number;
      loanCommitmentLcy: number;
      capitalCommitmentLcy: number;
      isCapitalContributionRuleOverride?: boolean;
    }>;
  };
}> = ({ equalisation }) => {
  const { currentSection } = useEqualisationSection();

  return (
    <>
      {currentSection === EqualisationSection.Commitments && (
        <AddCommitmentsToEqualisation
          savedEqualisation={
            equalisation
              ? {
                  id: equalisation.id,
                  closeDate: equalisation.closeDate,
                  selectedCommitments: equalisation.selectedCommitments,
                  name: equalisation.name,
                  isDraft: equalisation.isDraft,
                }
              : undefined
          }
        />
      )}
      {[
        EqualisationSection.Investments,
        EqualisationSection.Expenses,
        EqualisationSection.Income,
      ].includes(currentSection) &&
        equalisation && (
          <EqualiseActions baseCurrency={equalisation.baseCurrency} />
        )}
      {currentSection === EqualisationSection.ManagementFees &&
        equalisation && (
          <EqualiseFees baseCurrency={equalisation.baseCurrency} />
        )}
      {currentSection === EqualisationSection.CapitalContributions &&
        equalisation && (
          <EqualiseCapitalContributions
            baseCurrency={equalisation.baseCurrency}
          />
        )}
      {currentSection === EqualisationSection.ActionLinkedCapitalActions &&
        equalisation && (
          <EqualiseActionLinkedCapitalActions
            baseCurrency={equalisation.baseCurrency}
          />
        )}
      {currentSection === EqualisationSection.GeneralCapitalCalls &&
        equalisation && (
          <EqualiseGeneralCapitalCalls
            baseCurrency={equalisation.baseCurrency}
          />
        )}
      {currentSection === EqualisationSection.EqualisationInterest &&
        equalisation && (
          <EqualisationInterest baseCurrency={equalisation.baseCurrency} />
        )}
      {currentSection === EqualisationSection.Summary && (
        <EqualisationSummary />
      )}
    </>
  );
};

const EqualisationsEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const [equalisation] = api.equalisations.get.useSuspenseQuery({
    equalisationId: id ?? '',
    vehicleId: selectedVehicleId ?? '',
  });

  const [commitments] = api.commitments.list.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
    page: 1,
    rowsPerPage: 100000,
    order: SortOrder.Desc,
    orderBy: 'commitmentDate',
  });

  return (
    <EqualisationProvider
      commitments={commitments.results.map((commitment) => ({
        _id: commitment.id,
        isBalancingEntity: commitment.isBalancingEntity,
        totalCommitmentLcy: commitment.totalCommitmentLcy,
      }))}
      initialSection={equalisation.currentSection}
      sections={equalisation.sections}
      baseCurrency={equalisation.baseCurrency}
      closeDate={equalisation.closeDate}
    >
      <EqualisationsEditorContent equalisation={equalisation} />
    </EqualisationProvider>
  );
};

export default EqualisationsEditor;
